import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import FilteringTable from './Table/FilteringTable/FilteringTable';
import ColumnFilter from './Table/FilteringTable/ColumnFilter';
import PageTitle from '../layouts/PageTitle';

import { tableColumnNamesRefereeAdmin } from '../../utils/tableColumnNamesRefereeAdmin';
import { arrayToFlatObject } from '../../utils/arrayToFlatObject';
import { tableRowsValuesRefereeAdministration } from '../../utils/tableRowsValuesRefereeAdministration';
import { CommonCollections } from '../../App';
import AdminRefereeReportCell from './AdminRefereeReportCell';
import { filteringTableGetTableColumns } from '../../utils/filteringTableGetTableColumns';
import { useStaticCollections } from '../../context/StaticCollections';
import { useAdminCollections } from '../../context/AdminCollections';
import { useRefereeCollections } from '../../context/RefereeReportCollections';
import { getReportRoundYear } from '../../utils/getReportRoundYear';

const AdminRefereeReport = () => {
  const {
    fellows,
    declineReasons,
    nonAasReferees,
    nominationStatusTypes,
    refereeStatuses,
    sectionalCommitteesWithCmse,
    users,
    isLoading: staticLoading,
  } = useStaticCollections();

  const {
    candidates,
    nominationArray,
    nominationStateArray,
    refereeReportArchive,
    isLoading: adminLoading,
  } = useAdminCollections();

  const { setRefereeReportId } = useContext(CommonCollections);

  const { refereeReports, isLoading: refereeLoading } = useRefereeCollections();

  const [refereesData, setRefereesData] = useState([]);
  const [tableColumn, setTableColumn] = useState([]);
  const [filterInstance, setFilterInstance] = useState(null);
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [isEmailApiError, setIsEmailApiError] = useState(false);
  const [isNotificationTemporary, setIsNotificationTemporary] = useState(false);

  const location = useLocation();
  const isArchiveView = location.pathname.includes('referee-archive');
  const [componentName, setComponentName] = useState(
    isArchiveView ? 'ArchivedAdminRefereeReport' : 'AdminRefereeReport',
  );

  useEffect(() => {
    const newComponentName = isArchiveView
      ? 'ArchivedAdminRefereeReport'
      : 'AdminRefereeReport';
    if (newComponentName !== componentName) {
      setComponentName(newComponentName);
    }
  }, [location.pathname, isArchiveView, componentName]);

  useEffect(() => {
    const filteredTableColumnNamesRefereeAdmin = {
      ...tableColumnNamesRefereeAdmin,
    };
    if (isArchiveView) {
      delete filteredTableColumnNamesRefereeAdmin['Report email sent'];
      delete filteredTableColumnNamesRefereeAdmin['Reminder email sent'];
    }

    const tableColumns = filteringTableGetTableColumns(
      filteredTableColumnNamesRefereeAdmin,
      ColumnFilter,
      (props) => (
        <AdminRefereeReportCell
          {...props}
          onEmailApiErrorChange={(error) => {
            if (error) {
              setNotificationMessage(
                'Error: campaign Monitor email failed; try again.',
              );
              setNotificationTitle('');
              setIsNotificationTemporary(false);
            } else {
              setNotificationMessage('Email has been sent successfully!');
              setNotificationTitle('');
              setIsNotificationTemporary(true);
              setTimeout(() => setIsNotificationTemporary(false), 6000);
            }
            setIsEmailApiError(error);
          }}
          isArchiveView={isArchiveView}
        />
      ),
    );

    const reportsToUse = isArchiveView ? refereeReportArchive : refereeReports;

    const nominationLookup = new Map(
      nominationStateArray.map((state) => [
        state?.attributes?.nominationId,
        state,
      ]),
    );

    const refereeReportsWithRoundYear = reportsToUse.map((report) => {
      const nominationId = report?.attributes?.nominationId;
      const match = nominationLookup.get(nominationId);

      return {
        id: report.id,
        attributes: {
          ...report.attributes,
          roundYear: match?.attributes?.roundYear ?? '',
        },
      };
    });

    const tableData = tableRowsValuesRefereeAdministration(
      refereeReportsWithRoundYear,
      refereeStatuses,
      declineReasons,
    );

    setTableColumn(tableColumns);
    const refereeData = [
      tableData?.map((item) => item.nominationId),
      tableData,
    ];
    const nominationArr = nominationArray.map((item) => ({
      [item.id]: item.attributes,
    }));

    const nominationsObj = arrayToFlatObject(nominationArr);
    const tableDataTwo = Object.values(refereeData[1])?.map((referee) => {
      if (nominationsObj[referee.nominationId] !== undefined) {
        referee.candidate =
          candidates[nominationsObj[referee.nominationId].candidateId];
        referee.sectionalCommittee =
          sectionalCommitteesWithCmse[
            nominationsObj[referee.nominationId]?.sectionalCommitteeId ?? 14
          ];
        referee.nominationStatus =
          nominationStatusTypes[
            nominationsObj[referee.nominationId].nominationStatusTypeId
          ];
        referee.nominationStatusTypeId =
          nominationsObj[referee.nominationId].nominationStatusTypeId;
      }
      referee.refereeUser = referee.refereeFellowId
        ? fellows[referee.refereeFellowId]
        : nonAasReferees[referee.nonAasRefereeId];
      referee.reportFile = referee.report.data
        ? referee.report.data.attributes.url
        : null;
      referee.reportUploadedYear = getReportRoundYear(referee);

      return referee;
    });

    setRefereesData(tableDataTwo);
    setRefereeReportId(null);
  }, [
    refereeStatuses,
    sectionalCommitteesWithCmse,
    users,
    declineReasons,
    fellows,
    candidates,
    nonAasReferees,
    nominationArray,
    refereeReports,
    refereeReportArchive,
    setRefereeReportId,
    nominationStatusTypes,
    isArchiveView,
  ]);

  const resetNotification = useCallback(() => {
    if (isArchiveView) {
      setNotificationTitle('');
      setNotificationMessage('');
      return;
    }

    const pendingApproval = refereesData.filter(
      (item) => item.requestEmailDate === 'Pending approval',
    );

    const pendingApprovalCount = pendingApproval.length;
    const candidateNames = pendingApproval
      .map((entry) => ` ${entry.refereeUser} (${entry.candidate})`)
      .join('\n');

    setNotificationTitle(
      `Approval required for ${pendingApprovalCount} referees:`,
    );
    setNotificationMessage(candidateNames);
  }, [refereesData, isArchiveView]);

  useEffect(() => {
    if (!isEmailApiError && !isNotificationTemporary) {
      resetNotification();
    }
  }, [isEmailApiError, isNotificationTemporary, resetNotification]);

  return (
    <>
      <PageTitle
        activeMenu={
          isArchiveView ? 'Referee Archive' : 'Referee Administration'
        }
        motherMenu="Home"
        notificationMessage={notificationMessage}
        notificationTitle={notificationTitle}
        setFilterInstance={setFilterInstance}
      />
      <FilteringTable
        key={componentName}
        componentName={componentName}
        columns={tableColumn}
        data={refereesData}
        filterInstance={filterInstance}
        isLoading={staticLoading || adminLoading || refereeLoading}
      />
    </>
  );
};

export default AdminRefereeReport;
